// EditableItem<T> — wrap any LLM-produced item to make user editing first-class.
// Per LLM_NATIVE_ARCHITECTURE.md §2.3.
//
// Core assertion (§2.3 · MANIFESTO §1.3a 工程形态):
//   AI output is ALWAYS a draft, NEVER a final state. Users editing the AI's
//   mcq distractors / flashcard backs / case attribution / mindmap branches
//   is the core loop, not a patch feature. This module is the schema-level
//   commitment to that stance.
//
// Shape:
//   {
//     schemaVersion,
//     source: 'ai_generated' | 'user_authored' | 'imported',
//     history: [
//       { at, by, snapshot, note? },
//       ...
//     ],
//     current,    // === history[history.length - 1].snapshot
//   }
//
// UI default: render `current`. Provide:
//   - "撤销上次编辑" → pop history (only if length > 1)
//   - "恢复 AI 原版" → reset history to [history[0]] (the originating snapshot)
//   - inline edit → append new history entry { by: 'user', snapshot: edited, note? }
//
// IDB-storage policy (planned future):
//   results store entries are wrapped: { mcqs: EditableItem<MCQ>[], … }
//   Migration from legacy non-wrapped entries: wrap each item with
//   `toEditable(item, source: 'ai_generated', at: pkg.generatedAt ?? 'unknown')`.
import { z } from 'zod';
import { INGEST_PACKAGE_SCHEMA_VERSION } from './schema-version.js';
export const EditableSourceSchema = z.enum(['ai_generated', 'user_authored', 'imported']);
export const EditableHistoryEntrySchema = z.object({
    at: z.string(), // ISO 8601 datetime
    by: z.enum(['ai', 'user']),
    /** Snapshot of the item AT THIS POINT in history. Generic in TS, recorded
     *  as `unknown` here so we don't fight zod generics — wrappers cast back. */
    snapshot: z.unknown(),
    /** Optional user-authored note ("fixed typo in option C"). */
    note: z.string().optional(),
}).passthrough();
/**
 * Generic factory — produces a zod schema for `EditableItem<T>` given an
 * inner item schema. The result accepts the wrapped shape; consumers
 * cast `current` / `history[i].snapshot` back to T.
 *
 * Why factory (not a single fixed schema): the inner item types differ
 * (MCQ vs Flashcard vs Essay …) and zod can't generic over a parametric
 * type without a runtime witness. Factory takes the witness.
 */
export function editableItemSchema(itemSchema) {
    return z.object({
        schemaVersion: z.string().optional().default(INGEST_PACKAGE_SCHEMA_VERSION),
        source: EditableSourceSchema,
        history: z.array(z.object({
            at: z.string(),
            by: z.enum(['ai', 'user']),
            snapshot: itemSchema,
            note: z.string().optional(),
        })),
        current: itemSchema,
    });
}
// ── Helpers (pure, no IO) ──────────────────────────────────────────────────
/** Wrap a plain item into a fresh EditableItem with one history entry. */
export function toEditable(item, source = 'ai_generated', at = new Date().toISOString()) {
    return {
        schemaVersion: INGEST_PACKAGE_SCHEMA_VERSION,
        source,
        history: [{ at, by: source === 'ai_generated' ? 'ai' : 'user', snapshot: item }],
        current: item,
    };
}
/** Append a user edit. Returns a NEW EditableItem (immutable update). */
export function appendUserEdit(prev, edited, opts = {}) {
    return {
        ...prev,
        history: [
            ...prev.history,
            {
                at: opts.at ?? new Date().toISOString(),
                by: 'user',
                snapshot: edited,
                ...(opts.note !== undefined ? { note: opts.note } : {}),
            },
        ],
        current: edited,
    };
}
/** Pop the last user edit. Returns a NEW EditableItem with one entry removed,
 *  OR the same EditableItem if there's nothing to undo (history.length <= 1). */
export function undoLastEdit(prev) {
    if (prev.history.length <= 1)
        return prev;
    const newHistory = prev.history.slice(0, -1);
    return {
        ...prev,
        history: newHistory,
        current: newHistory[newHistory.length - 1].snapshot,
    };
}
/** Reset to the original (history[0]). Returns a NEW EditableItem. */
export function revertToOriginal(prev) {
    if (prev.history.length === 0)
        return prev;
    const original = prev.history[0];
    return {
        ...prev,
        history: [original],
        current: original.snapshot,
    };
}
/** True iff at least one user edit has been recorded. */
export function hasUserEdits(item) {
    return item.history.some((h) => h.by === 'user');
}
//# sourceMappingURL=editable-item.js.map